import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, FileText, Eye, Pencil, Trash2, Search, Clock, CheckCircle, FileEdit } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogManager({ user }) {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/cms/blogs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogs(res.data);
    } catch (err) {
      console.error('Error fetching blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/cms/blogs/${blogId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchBlogs();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error deleting blog');
    }
  };

  const totalBlogs = blogs.length;
  const draftBlogs = blogs.filter(b => b.status === 'draft').length;
  const publishedBlogs = blogs.filter(b => b.status === 'published').length;

  const filtered = blogs.filter(b =>
    b.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    b.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading blogs...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeInUp" data-testid="blog-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Blog Manager
          </h1>
          <p className="text-slate-500 mt-1">Create and manage website blog posts</p>
        </div>
        <button
          onClick={() => navigate('/dashboard/blogs/new')}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium transition-all hover:shadow-lg hover:shadow-violet-500/30"
          data-testid="add-blog-btn"
        >
          <Plus className="w-4 h-4" />
          Add Blog
        </button>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="metric-total">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <div className="text-sm text-slate-500">Total Blogs</div>
              <div className="text-2xl font-bold text-slate-900">{totalBlogs}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="metric-drafts">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <FileEdit className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <div className="text-sm text-slate-500">Draft Blogs</div>
              <div className="text-2xl font-bold text-slate-900">{draftBlogs}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="metric-published">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <div className="text-sm text-slate-500">Published Blogs</div>
              <div className="text-2xl font-bold text-slate-900">{publishedBlogs}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search blogs by title or slug..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200/60 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-300"
          data-testid="blog-search"
        />
      </div>

      {/* Blog Table */}
      <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="blog-table">
            <thead className="bg-slate-50/80 border-b border-slate-200/60">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Blog</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Slug</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Published</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((blog) => (
                <tr key={blog.id} className="hover:bg-violet-50/30 transition-colors" data-testid={`blog-row-${blog.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      {blog.featured_image ? (
                        <img src={blog.featured_image} alt="" className="w-12 h-12 rounded-lg object-cover border border-slate-200" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-slate-400" />
                        </div>
                      )}
                      <div className="font-medium text-slate-900 max-w-[200px] truncate">{blog.title}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500 font-mono">/{blog.slug}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      blog.status === 'published'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                    }`} data-testid={`blog-status-${blog.id}`}>
                      {blog.status === 'published' ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      {blog.created_at ? new Date(blog.created_at).toLocaleDateString() : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {blog.published_at ? new Date(blog.published_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1">
                      {blog.status === 'published' && (
                        <button
                          onClick={() => window.open(`/blog/${blog.slug}`, '_blank')}
                          className="p-2 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                          title="View on website"
                          data-testid={`view-blog-${blog.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/dashboard/blogs/edit/${blog.id}`)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                        data-testid={`edit-blog-${blog.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(blog.id)}
                        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                        data-testid={`delete-blog-${blog.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="text-center py-16">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No blogs found</p>
              <p className="text-slate-400 text-sm mt-1">Create your first blog post to get started</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
